import { t } from 'i18next';
import { FC } from 'react';
import { useFormContext } from 'react-hook-form';
import {
  StopRegistryShelterCondition,
  StopRegistryShelterElectricity,
  StopRegistryShelterType,
} from '../../../../../generated/graphql';
import {
  mapStopRegistryShelterConditionEnumToUiName,
  mapStopRegistryShelterElectricityEnumToUiName,
  mapStopRegistryShelterTypeEnumToUiName,
} from '../../../../../utils/i18n';
import { SimpleButton } from '../../../../common/Buttons';
import {
  EnumDropdown,
  NullableBooleanDropdown,
} from '../../../../common/Dropdowns';
import { InputElement, InputField } from '../../../../common/Inputs';
import { Column, Row } from '../../../../common/LayoutComponents';
import { SheltersFormState } from './schema';

const testIds = {
  shelterNumber: 'ShelterFormFields::shelterNumber',
  shelterType: 'ShelterFormFields::shelterType',
  shelterElectricity: 'ShelterFormFields::shelterElectricity',
  shelterLighting: 'ShelterFormFields::shelterLighting',
  shelterCondition: 'ShelterFormFields::shelterCondition',
  timetableCabinets: 'ShelterFormFields::timetableCabinets',
  trashCan: 'ShelterFormFields::trashCan',
  shelterHasDisplay: 'ShelterFormFields::shelterHasDisplay',
  bicycleParking: 'ShelterFormFields::bicycleParking',
  leaningRail: 'ShelterFormFields::leaningRail',
  outsideBench: 'ShelterFormFields::outsideBench',
  shelterFasciaBoardTaping: 'ShelterFormFields::shelterFasciaBoardTaping',
  shelterExternalId: 'ShelterFormFields::shelterExternalId',
  deleteShelter: 'ShelterFormFields::deleteShelter',
  copyShelter: 'ShelterFormFields::copyShelter',
};

type ShelterFormFieldsProps = {
  readonly index: number;
  readonly onRemove: (index: number) => void;
  readonly onCopy: (shelter: number) => void;
};

export const ShelterFormFields: FC<ShelterFormFieldsProps> = ({
  index,
  onRemove,
  onCopy,
}) => {
  const { register, watch } = useFormContext<SheltersFormState>();
  const toBeDeleted = watch(`shelters.${index}.toBeDeleted`);

  return (
    <Column className="gap-4">
      <Row className="flex-wrap items-end gap-4">
        <InputField<SheltersFormState>
          type="number"
          translationPrefix="stopDetails"
          min={0}
          fieldPath={`shelters.${index}.shelterNumber`}
          inputClassName="w-20"
          testId={testIds.shelterNumber}
          disabled={toBeDeleted}
        />
        <InputField<SheltersFormState>
          type="string"
          translationPrefix="stopDetails"
          fieldPath={`shelters.${index}.shelterExternalId`}
          inputClassName="w-20"
          testId={testIds.shelterExternalId}
          disabled={toBeDeleted}
        />
        <InputField<SheltersFormState>
          translationPrefix="stopDetails"
          fieldPath={`shelters.${index}.shelterType`}
          testId={testIds.shelterType}

          inputElementRenderer={(props) => (
            <EnumDropdown<StopRegistryShelterType>
              enumType={StopRegistryShelterType}
              placeholder={t(($) => $.unknown)}
              uiNameMapper={(value) =>
                mapStopRegistryShelterTypeEnumToUiName(t, value)
              }
              buttonClassName="min-w-36"
              includeNullOption
              disabled={toBeDeleted}
              {...props}
            />
          )}
        />
        <InputField<SheltersFormState>
          translationPrefix="stopDetails"
          fieldPath={`shelters.${index}.shelterElectricity`}
          testId={testIds.shelterElectricity}

          inputElementRenderer={(props) => (
            <EnumDropdown<StopRegistryShelterElectricity>
              enumType={StopRegistryShelterElectricity}
              placeholder={t(($) => $.unknown)}
              uiNameMapper={(value) =>
                mapStopRegistryShelterElectricityEnumToUiName(t, value)
              }
              buttonClassName="min-w-44"
              includeNullOption
              disabled={toBeDeleted}
              {...props}
            />
          )}
        />
        <InputField<SheltersFormState, boolean | null>
          translationPrefix="stopDetails"
          fieldPath={`shelters.${index}.shelterLighting`}
          testId={testIds.shelterLighting}

          inputElementRenderer={(props) => (
            <NullableBooleanDropdown
              placeholder={t(($) => $.unknown)}
              buttonClassName="min-w-32"
              disabled={toBeDeleted}
              {...props}
            />
          )}
        />
        <InputField<SheltersFormState>
          translationPrefix="stopDetails"
          fieldPath={`shelters.${index}.shelterCondition`}
          testId={testIds.shelterCondition}

          inputElementRenderer={(props) => (
            <EnumDropdown<StopRegistryShelterCondition>
              enumType={StopRegistryShelterCondition}
              placeholder={t(($) => $.unknown)}
              uiNameMapper={(value) =>
                mapStopRegistryShelterConditionEnumToUiName(t, value)
              }
              includeNullOption
              disabled={toBeDeleted}
              buttonClassName="min-w-32"
              {...props}
            />
          )}
        />
        <InputField<SheltersFormState>
          type="number"
          translationPrefix="stopDetails"
          min={0}
          fieldPath={`shelters.${index}.timetableCabinets`}
          inputClassName="w-20"
          testId={testIds.timetableCabinets}
          disabled={toBeDeleted}
        />
      </Row>
      <Row className="flex-wrap items-end gap-4">
        <label
          htmlFor={`trashCan-${index}`}
          className="inline-flex font-normal"
        >
          <InputElement<SheltersFormState>
            type="checkbox"
            id={`trashCan-${index}`}
            fieldPath={`shelters.${index}.trashCan`}
            className="mr-2 h-6 w-6"
            testId={testIds.trashCan}
            disabled={toBeDeleted}
          />
          {t(($) => $.stopDetails.shelters.trashCan)}
        </label>

        <label
          htmlFor={`shelterHasDisplay-${index}`}
          className="inline-flex font-normal"
        >
          <InputElement<SheltersFormState>
            type="checkbox"
            id={`shelterHasDisplay-${index}`}
            fieldPath={`shelters.${index}.shelterHasDisplay`}
            className="mr-2 h-6 w-6"
            testId={testIds.shelterHasDisplay}
            disabled={toBeDeleted}
          />
          {t(($) => $.stopDetails.shelters.shelterHasDisplay)}
        </label>

        <label
          htmlFor={`bicycleParking-${index}`}
          className="inline-flex font-normal"
        >
          <InputElement<SheltersFormState>
            type="checkbox"
            id={`bicycleParking-${index}`}
            fieldPath={`shelters.${index}.bicycleParking`}
            className="mr-2 h-6 w-6"
            testId={testIds.bicycleParking}
            disabled={toBeDeleted}
          />
          {t(($) => $.stopDetails.shelters.bicycleParking)}
        </label>

        <label
          htmlFor={`leaningRail-${index}`}
          className="inline-flex font-normal"
        >
          <InputElement<SheltersFormState>
            type="checkbox"
            id={`leaningRail-${index}`}
            fieldPath={`shelters.${index}.leaningRail`}
            className="mr-2 h-6 w-6"
            testId={testIds.leaningRail}
            disabled={toBeDeleted}
          />
          {t(($) => $.stopDetails.shelters.leaningRail)}
        </label>

        <label
          htmlFor={`outsideBench-${index}`}
          className="inline-flex font-normal"
        >
          <InputElement<SheltersFormState>
            type="checkbox"
            id={`outsideBench-${index}`}
            fieldPath={`shelters.${index}.outsideBench`}
            className="mr-2 h-6 w-6"
            testId={testIds.outsideBench}
            disabled={toBeDeleted}
          />
          {t(($) => $.stopDetails.shelters.outsideBench)}
        </label>

        <label
          htmlFor={`shelterFasciaBoardTaping-${index}`}
          className="inline-flex font-normal"
        >
          <InputElement<SheltersFormState>
            type="checkbox"
            id={`shelterFasciaBoardTaping-${index}`}
            fieldPath={`shelters.${index}.shelterFasciaBoardTaping`}
            className="mr-2 h-6 w-6"
            testId={testIds.shelterFasciaBoardTaping}
            disabled={toBeDeleted}
          />
          {t(($) => $.stopDetails.shelters.shelterFasciaBoardTaping)}
        </label>
      </Row>
      <input
        type="checkbox"
        hidden
        {...register(`shelters.${index}.toBeDeleted`)}
      />
      <div className="grid auto-cols-max grid-flow-col items-center gap-4">
        <SimpleButton
          shape="slim"
          testId={testIds.deleteShelter}
          onClick={() => onRemove(index)}
          inverted
        >
          {t(($) =>
            toBeDeleted
              ? $.stopDetails.shelters.cancelDeleteShelter
              : $.stopDetails.shelters.deleteShelter,
          )}
        </SimpleButton>
        <SimpleButton
          shape="slim"
          testId={testIds.copyShelter}
          onClick={() => onCopy(index)}
          inverted
        >
          {t(($) => $.stopDetails.shelters.copyShelter)}
        </SimpleButton>
      </div>
    </Column>
  );
};
